import { QueryClient, queryOptions, useQuery } from '@tanstack/react-query';

import { apiUserFetch } from '..';
import { USER_DASHBOARD_QUERY_KEYS } from '../user-dashboard/queries';
import { AuthUserResponse } from './mutations';
import { QuestionnaireData, QuestionnaireQuestionsResponse, QuestionnaireStatus } from './types';

export const AUTH_QUERY_KEYS = {
  user: 'user',
  balance: 'balance',
  transaction: 'transaction',
  questionnaire: 'questionnaire',
  questionnaireQuestions: 'questionnaire-questions',
  questionnaireStatus: 'questionnaire-status',
  notifications: 'notifications',
  authFeatures: 'auth-features',
};

export interface AuthFeatures {
  cognitoEnabled: boolean;
  maintenanceEnabled?: boolean;
}

export const _getAuthFeatures = () => {
  return queryOptions({
    queryKey: [AUTH_QUERY_KEYS.authFeatures],
    queryFn: () =>
      apiUserFetch<AuthFeatures>('/auth/features', {
        method: 'GET',
      }),
    retry: false,
    retryOnMount: false,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const _getMeOptions = () => {
  return queryOptions({
    queryKey: [AUTH_QUERY_KEYS.user],
    queryFn: () =>
      apiUserFetch<AuthUserResponse>('/auth/me', {
        method: 'GET',
      }),
    retry: false,
  });
};

export const _getQuestionnaire = () => {
  return queryOptions({
    queryKey: [AUTH_QUERY_KEYS.questionnaire],
    queryFn: () =>
      apiUserFetch<QuestionnaireData>('/questionnaire', {
        method: 'GET',
      }),
  });
};

export const _getQuestionnaireByUserId = (userId: string) => {
  return queryOptions({
    queryKey: [AUTH_QUERY_KEYS.questionnaire, userId],
    queryFn: () =>
      apiUserFetch<QuestionnaireData>(`/questionnaire/${userId}`, {
        method: 'GET',
      }),
  });
};

export const _getQuestionnaireQuestions = () => {
  return queryOptions({
    queryKey: [AUTH_QUERY_KEYS.questionnaireQuestions],
    queryFn: () =>
      apiUserFetch<QuestionnaireQuestionsResponse>('/questionnaire/questions', {
        method: 'GET',
      }),
  });
};

export const _getQuestionnaireStatus = () => {
  return queryOptions({
    queryKey: [AUTH_QUERY_KEYS.questionnaireStatus],
    queryFn: () =>
      apiUserFetch<QuestionnaireStatus>('/questionnaire/status', {
        method: 'GET',
      }),
    retry: false,
  });
};

//HOOKS
export function useGetUser() {
  const { data: user, ...rest } = useQuery(_getMeOptions());

  return { user, ...rest, isDemo: !!user?.isDemo };
}

export function useAuthFeatures() {
  return useQuery(_getAuthFeatures());
}

/**
 * Cached answers to "who is signed in and what do they still owe us". Each is keyed only by its own
 * name, so a session change must reset them or the next user is routed on the previous user's data.
 */
export const resetSessionScopedQueries = (queryClient: QueryClient) => {
  const keys = [
    USER_DASHBOARD_QUERY_KEYS.userProfile,
    AUTH_QUERY_KEYS.questionnaireStatus,
    AUTH_QUERY_KEYS.questionnaire,
    AUTH_QUERY_KEYS.questionnaireQuestions,
  ];

  for (const queryKey of keys) {
    queryClient.resetQueries({ queryKey: [queryKey] });
  }
};
